import { supabase } from '../lib/supabase';
import { clientValidation } from '../utils/validationUtils';

export const clientService = {
  async getAll(userId) {
    try {
      if (!userId) {
        throw new Error('معرف المستخدم مطلوب');
      }

      const { data, error } = await supabase?.from('clients')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching clients:', error);
        throw new Error(`فشل في تحميل العملاء: ${error?.message || 'خطأ غير معروف'}`);
      }

      if (!data) {
        return [];
      }

      return data?.map(row => ({
        id: row?.id,
        name: row?.name,
        email: row?.email,
        phone: row?.phone,
        category: row?.category,
        status: row?.status,
        preferredLocation: row?.preferred_location,
        budgetMin: row?.budget_min,
        budgetMax: row?.budget_max,
        propertyType: row?.property_type,
        bedrooms: row?.bedrooms,
        source: row?.source,
        assignedAgent: row?.assigned_agent,
        priority: row?.priority,
        notes: row?.notes,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      }));
    } catch (error) {
      console.error('Client service error:', error);
      throw error;
    }
  },

  async create(client, userId) {
    try {
      if (!userId) {
        throw new Error('معرف المستخدم مطلوب');
      }

      // SERVER-SIDE VALIDATION
      const validation = clientValidation?.validate(client);
      if (!validation?.isValid) {
        const errorMessages = Object.values(validation?.errors)?.join('، ');
        throw new Error(`خطأ في البيانات: ${errorMessages}`);
      }

      if (!client?.name) {
        throw new Error('اسم العميل مطلوب');
      }

      if (!client?.phone && !client?.email) {
        throw new Error('رقم الهاتف أو البريد الإلكتروني مطلوب');
      }

      const { data, error } = await supabase?.from('clients')?.insert({
          user_id: userId,
          name: client?.name,
          email: client?.email,
          phone: client?.phone,
          category: client?.category,
          status: client?.status || 'active',
          preferred_location: client?.preferredLocation,
          budget_min: client?.budgetMin,
          budget_max: client?.budgetMax,
          property_type: client?.propertyType,
          bedrooms: client?.bedrooms,
          source: client?.source,
          assigned_agent: client?.assignedAgent,
          priority: client?.priority || 'medium',
          notes: client?.notes
        })?.select()?.single();

      if (error) {
        console.error('Error creating client:', error);
        throw new Error(`فشل في إنشاء العميل: ${error?.message || 'خطأ غير معروف'}`);
      }

      return {
        id: data?.id,
        name: data?.name,
        email: data?.email,
        phone: data?.phone,
        category: data?.category,
        status: data?.status,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Client service error:', error);
      throw error;
    }
  },

  async update(clientId, updates, userId) {
    try {
      if (!userId) {
        throw new Error('معرف المستخدم مطلوب');
      }

      if (!clientId) {
        throw new Error('معرف العميل مطلوب');
      }

      // SERVER-SIDE VALIDATION for updates
      if (Object.keys(updates)?.length > 0) {
        const validation = clientValidation?.validate(updates);
        if (!validation?.isValid) {
          const errorMessages = Object.values(validation?.errors)?.join('، ');
          throw new Error(`خطأ في البيانات: ${errorMessages}`);
        }
      }

      const updateData = {};
      if (updates?.name) updateData.name = updates?.name;
      if (updates?.email) updateData.email = updates?.email;
      if (updates?.phone) updateData.phone = updates?.phone;
      if (updates?.category) updateData.category = updates?.category;
      if (updates?.status) updateData.status = updates?.status;
      if (updates?.preferredLocation) updateData.preferred_location = updates?.preferredLocation;
      if (updates?.budgetMin !== undefined) updateData.budget_min = updates?.budgetMin;
      if (updates?.budgetMax !== undefined) updateData.budget_max = updates?.budgetMax;
      if (updates?.propertyType) updateData.property_type = updates?.propertyType;
      if (updates?.bedrooms !== undefined) updateData.bedrooms = updates?.bedrooms;
      if (updates?.source) updateData.source = updates?.source;
      if (updates?.assignedAgent) updateData.assigned_agent = updates?.assignedAgent;
      if (updates?.priority) updateData.priority = updates?.priority;
      if (updates?.notes !== undefined) updateData.notes = updates?.notes;

      if (Object.keys(updateData)?.length === 0) {
        throw new Error('لا توجد بيانات للتحديث');
      }

      const { data, error } = await supabase?.from('clients')?.update(updateData)?.eq('id', clientId)?.eq('user_id', userId)?.select()?.single();

      if (error) {
        console.error('Error updating client:', error);
        throw new Error(`فشل في تحديث العميل: ${error?.message || 'خطأ غير معروف'}`);
      }

      return data;
    } catch (error) {
      console.error('Client service error:', error);
      throw error;
    }
  },

  async delete(clientId, userId) {
    try {
      if (!userId) {
        throw new Error('معرف المستخدم مطلوب');
      }

      if (!clientId) {
        throw new Error('معرف العميل مطلوب');
      }

      const { error } = await supabase?.from('clients')?.delete()?.eq('id', clientId)?.eq('user_id', userId);

      if (error) {
        console.error('Error deleting client:', error);
        throw new Error(`فشل في حذف العميل: ${error?.message || 'خطأ غير معروف'}`);
      }
    } catch (error) {
      console.error('Client service error:', error);
      throw error;
    }
  }
};